import React, { useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { isDevNoteVisible } from "../DevNote";

/**
 * 分頁面板（design system primitive）。
 * 檢視頁/詳情頁（如硬體五類資訊、合約基本+關聯+資產）常用。
 *
 *   <Tabs tabs={[
 *     { key:"basic", label:"基本資訊", content:<Basic/> },
 *     { key:"relation", label:"關聯合約", content:<Relation/>, badge:3 },
 *   ]} />
 *
 * 不傳 activeKey 時內部自管；傳 activeKey/onChange 則受控。
 * 內容放在**分頁籤外面**（像訊息中心那樣整段 feed 共用一個面板）時，
 * 把它當 children 傳進來，會包成單一 `tabpanel` 綁到目前這個籤。
 *
 * ## 無障礙（票 36）
 *
 * 分頁籤要真的是分頁籤：`tablist`／`tab`／`tabpanel` ＋ `aria-selected`，
 * 籤本身用 `<button>`（不是沒有 href 的 `<a>` —— 那種鍵盤 Tab 走不到、
 * 讀螢幕的人也聽不出那是分頁籤，訊息中心原本就是這樣）。
 * 鍵盤依 WAI-ARIA tabs pattern：左右鍵換籤、Home／End 跳頭尾，
 * 沒選到的籤 `tabIndex=-1`，整組只佔一個 Tab 停留點。
 */
export const Tabs = ({ tabs: allTabs, activeKey, onChange, className, navClassName, children }) => {
  // devOnly 的分頁(尚未開放的佔位:專案關聯/合約關聯/維護紀錄…)只在 development 出現,
  // 部署站連分頁都不長出來(2026-09-09 裁示:尚未開放的區塊使用者不該看到)
  const tabs = allTabs.filter((t) => !t.devOnly || isDevNoteVisible());
  const [internal, setInternal] = useState(tabs[0] && tabs[0].key);
  const active = activeKey !== undefined ? activeKey : internal;
  const baseId = useId();
  const tabId = (key) => `tab-${baseId}-${key}`;
  const panelId = (key) => `tabpanel-${baseId}-${key}`;
  const btnRefs = useRef({});

  const setActive = (k) => {
    if (onChange) onChange(k);
    if (activeKey === undefined) setInternal(k);
  };

  // WAI-ARIA tabs pattern:左右鍵換籤並把焦點帶過去(停用的籤跳過)
  const onKeyDown = (e) => {
    const usable = tabs.filter((t) => !t.disabled);
    if (!usable.length) return;
    const at = Math.max(0, usable.findIndex((t) => t.key === active));
    const go = (i) => {
      e.preventDefault();
      const next = usable[(i + usable.length) % usable.length];
      setActive(next.key);
      const el = btnRefs.current[next.key];
      if (el) el.focus();
    };
    if (e.key === "ArrowRight" || e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") go(at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(usable.length - 1);
  };

  return (
    <div className={className}>
      <Nav tabs role="tablist" className={navClassName || "mb-3"} onKeyDown={onKeyDown}>
        {tabs.map((t) => (
          <NavItem key={t.key} role="presentation">
            <NavLink
              tag="button"
              type="button"
              role="tab"
              id={tabId(t.key)}
              aria-selected={active === t.key}
              aria-controls={children ? panelId(active) : panelId(t.key)}
              tabIndex={active === t.key ? 0 : -1}
              innerRef={(el) => { btnRefs.current[t.key] = el; }}
              className={classnames({ active: active === t.key })}
              disabled={t.disabled}
              onClick={() => setActive(t.key)}
            >
              {t.icon ? <i className={`mdi ${t.icon} me-1`} /> : null}
              {t.label}
              {t.badge != null ? <span className="badge bg-light text-muted ms-1">{t.badge}</span> : null}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      {children ? (
        // 單一面板:內容由呼叫端自己依 activeKey 決定(訊息中心的 feed 就是這種)
        <div role="tabpanel" id={panelId(active)} aria-labelledby={tabId(active)} tabIndex={0}>
          {children}
        </div>
      ) : (
        <TabContent activeTab={active}>
          {tabs.map((t) => (
            <TabPane
              tabId={t.key}
              key={t.key}
              role="tabpanel"
              id={panelId(t.key)}
              aria-labelledby={tabId(t.key)}
              tabIndex={0}
              // 沒被選到的面板要從無障礙樹上拿掉,不然讀螢幕的人會聽到三份內容
              hidden={active !== t.key}
            >
              {active === t.key ? t.content : null}
            </TabPane>
          ))}
        </TabContent>
      )}
    </div>
  );
};
Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      devOnly: PropTypes.bool,
      label: PropTypes.node.isRequired,
      content: PropTypes.node,
      icon: PropTypes.string,
      badge: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      disabled: PropTypes.bool,
    })
  ).isRequired,
  activeKey: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  /** 分頁籤列的 class（預設 `mb-3`；訊息中心對稿是 `mb-2`） */
  navClassName: PropTypes.string,
  /** 給了就走單一面板模式：內容共用一個 tabpanel，綁到目前這個籤 */
  children: PropTypes.node,
};

export default Tabs;
